import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMailAccounts,
  createMailAccount,
  deleteMailAccount,
  syncMailAccount,
  syncMailAccountById,
  getAliases,
  createAlias,
  deleteAlias,
  MailAccount,
  MailAlias,
} from '../api/auth';
import {
  createFilterRule,
  createFolder,
  deleteFilterRule,
  deleteGpgKey,
  exportGpgKey,
  generateGpgKey,
  getFilterRules,
  getFolders,
  getGpgKeys,
  importGpgKey,
  GpgKey,
} from '../api/mailbox';
import GpgPassphraseDialog from '../components/GpgPassphraseDialog';

function MailAccountTab() {
  const qc = useQueryClient();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [folderName, setFolderName] = useState('');
  const [form, setForm] = useState({
    name: '',
    email_address: '',
    username: '',
    password: '',
    imap_host: '',
    imap_port: '993',
    imap_use_ssl: true,
    smtp_host: '',
    smtp_port: '587',
    smtp_use_tls: true,
  });
  const [aliasForm, setAliasForm] = useState({
    account: '' as string | number,
    email_address: '',
    display_name: '',
    is_default: false,
  });

  const { data: accountsData, isLoading } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: getMailAccounts,
  });
  const accounts = accountsData?.results || [];

  const { data: aliasesData } = useQuery({
    queryKey: ['aliases'],
    queryFn: () => getAliases(),
  });
  const aliases = aliasesData?.results || [];

  const syncMutation = useMutation({
    mutationFn: (id?: number) => (id ? syncMailAccountById(id) : syncMailAccount()),
    onSuccess: () => {
      setMessage('Mailbox sync queued.');
      setError('');
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['live-folders'] });
      qc.invalidateQueries({ queryKey: ['live-threads'] });
    },
    onError: (err: any) => setError(err?.response?.data?.detail || 'Sync failed'),
  });

  const createAccountMutation = useMutation({
    mutationFn: () =>
      createMailAccount({
        ...form,
        imap_port: Number(form.imap_port),
        smtp_port: Number(form.smtp_port),
      }),
    onSuccess: () => {
      setMessage('Mail account added.');
      setError('');
      setForm({
        name: '',
        email_address: '',
        username: '',
        password: '',
        imap_host: '',
        imap_port: '993',
        imap_use_ssl: true,
        smtp_host: '',
        smtp_port: '587',
        smtp_use_tls: true,
      });
      qc.invalidateQueries({ queryKey: ['mail-accounts'] });
    },
    onError: (err: any) => setError(JSON.stringify(err?.response?.data || 'Error')),
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (id: number) => deleteMailAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail-accounts'] }),
    onError: (err: any) => setError(err?.response?.data?.detail || 'Could not remove account'),
  });

  const createFolderMutation = useMutation({
    mutationFn: () => createFolder({ name: folderName.trim() }),
    onSuccess: () => {
      setFolderName('');
      setMessage('Folder created.');
      setError('');
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['live-folders'] });
      qc.invalidateQueries({ queryKey: ['live-threads'] });
    },
    onError: (err: any) => setError(err?.response?.data?.detail || 'Could not create folder'),
  });

  const createAliasMutation = useMutation({
    mutationFn: () =>
      createAlias({
        account: Number(aliasForm.account),
        email_address: aliasForm.email_address,
        display_name: aliasForm.display_name,
        is_default: aliasForm.is_default,
      }),
    onSuccess: () => {
      setAliasForm({ account: '', email_address: '', display_name: '', is_default: false });
      setMessage('Alias added.');
      qc.invalidateQueries({ queryKey: ['aliases'] });
      qc.invalidateQueries({ queryKey: ['mail-accounts'] });
    },
    onError: (err: any) => setError(JSON.stringify(err?.response?.data || 'Error')),
  });

  const deleteAliasMutation = useMutation({
    mutationFn: (id: number) => deleteAlias(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aliases'] });
      qc.invalidateQueries({ queryKey: ['mail-accounts'] });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Mail accounts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The primary account is configured in <code>backend/.env</code>. You can add more IMAP accounts below.
      </Typography>

      {accounts.map((acc: MailAccount) => (
        <Paper key={acc.id} sx={{ p: 2, mb: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography fontWeight={600}>
                {acc.name} {acc.is_primary ? '(primary)' : ''}
              </Typography>
              <Typography variant="body2">{acc.email_address}</Typography>
              <Typography variant="body2" color="text.secondary">
                IMAP {acc.imap_host}:{acc.imap_port} · SMTP {acc.smtp_host}:{acc.smtp_port}
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<SyncIcon />}
              onClick={() => syncMutation.mutate(acc.id)}
              disabled={syncMutation.isPending}
            >
              Sync
            </Button>
            {!acc.is_primary && (
              <Button
                size="small"
                color="error"
                onClick={() => deleteAccountMutation.mutate(acc.id)}
              >
                Remove
              </Button>
            )}
          </Stack>
        </Paper>
      ))}

      <Button
        sx={{ mt: 1, mb: 3 }}
        variant="outlined"
        startIcon={<SyncIcon />}
        onClick={() => syncMutation.mutate(undefined)}
        disabled={syncMutation.isPending}
      >
        Sync all accounts
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Add mail account
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(['name', 'email_address', 'username', 'password', 'imap_host', 'smtp_host'] as const).map((f) => (
          <TextField
            key={f}
            size="small"
            label={f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            type={f === 'password' ? 'password' : 'text'}
            value={(form as any)[f]}
            onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))}
          />
        ))}
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            label="IMAP Port"
            type="number"
            value={form.imap_port}
            onChange={(e) => setForm((p) => ({ ...p, imap_port: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.imap_use_ssl}
                onChange={(e) => setForm((p) => ({ ...p, imap_use_ssl: e.target.checked }))}
              />
            }
            label="IMAP SSL"
          />
          <TextField
            size="small"
            label="SMTP Port"
            type="number"
            value={form.smtp_port}
            onChange={(e) => setForm((p) => ({ ...p, smtp_port: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.smtp_use_tls}
                onChange={(e) => setForm((p) => ({ ...p, smtp_use_tls: e.target.checked }))}
              />
            }
            label="SMTP TLS"
          />
        </Stack>
        <Button
          variant="contained"
          onClick={() => createAccountMutation.mutate()}
          disabled={createAccountMutation.isPending || !form.email_address || !form.password}
        >
          {createAccountMutation.isPending ? <CircularProgress size={20} /> : 'Add account'}
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Send-as aliases
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Addresses your provider allows you to send from (aliases / catch-alls).
      </Typography>
      {aliases.map((a: MailAlias) => (
        <Paper key={a.id} sx={{ p: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={600}>
              {a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
              {a.is_default ? ' (default)' : ''}
            </Typography>
          </Box>
          <Button size="small" color="error" onClick={() => deleteAliasMutation.mutate(a.id)}>
            Delete
          </Button>
        </Paper>
      ))}
      <Stack spacing={2} sx={{ mt: 2, mb: 3 }}>
        <TextField
          select
          size="small"
          label="Account"
          value={aliasForm.account}
          onChange={(e) => setAliasForm((p) => ({ ...p, account: e.target.value }))}
        >
          {accounts.map((a) => (
            <MenuItem key={a.id} value={a.id}>{a.email_address}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Alias email"
          value={aliasForm.email_address}
          onChange={(e) => setAliasForm((p) => ({ ...p, email_address: e.target.value }))}
        />
        <TextField
          size="small"
          label="Display name"
          value={aliasForm.display_name}
          onChange={(e) => setAliasForm((p) => ({ ...p, display_name: e.target.value }))}
        />
        <FormControlLabel
          control={
            <Switch
              checked={aliasForm.is_default}
              onChange={(e) => setAliasForm((p) => ({ ...p, is_default: e.target.checked }))}
            />
          }
          label="Default From address"
        />
        <Button
          variant="contained"
          onClick={() => createAliasMutation.mutate()}
          disabled={!aliasForm.account || !aliasForm.email_address || createAliasMutation.isPending}
        >
          Add alias
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Create folder
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          size="small"
          label="Folder name"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          fullWidth
        />
        <Button
          variant="outlined"
          onClick={() => createFolderMutation.mutate()}
          disabled={!folderName.trim() || createFolderMutation.isPending}
        >
          Create
        </Button>
      </Stack>
    </Box>
  );
}

function FilterRulesTab() {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    condition_field: 'from_contains',
    condition_value: '',
    action: 'mark_read',
    action_folder: '' as string | number,
    is_active: true,
  });

  const { data } = useQuery({
    queryKey: ['filter-rules'],
    queryFn: getFilterRules,
  });

  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: () => getFolders({ page_size: 200 }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createFilterRule({
        name: form.name,
        condition_field: form.condition_field,
        condition_value: form.condition_value,
        action: form.action,
        action_folder: form.action === 'move' && form.action_folder
          ? Number(form.action_folder)
          : null,
        is_active: form.is_active,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filter-rules'] });
      setForm({
        name: '',
        condition_field: 'from_contains',
        condition_value: '',
        action: 'mark_read',
        action_folder: '',
        is_active: true,
      });
      setError('');
    },
    onError: (err: any) => setError(JSON.stringify(err?.response?.data || 'Error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteFilterRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['filter-rules'] }),
  });

  const folders = foldersData?.results || [];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {(data?.results || []).map((rule) => (
        <Paper key={rule.id} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={600}>{rule.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {rule.condition_field} contains &quot;{rule.condition_value}&quot; → {rule.action}
            </Typography>
          </Box>
          <Button color="error" size="small" onClick={() => deleteMutation.mutate(rule.id)}>
            Delete
          </Button>
        </Paper>
      ))}
      {(data?.results || []).length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>No filter rules configured.</Typography>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Add filter rule
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Name"
          size="small"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        />
        <TextField
          select
          label="Condition"
          size="small"
          value={form.condition_field}
          onChange={(e) => setForm((p) => ({ ...p, condition_field: e.target.value }))}
        >
          <MenuItem value="from_contains">From contains</MenuItem>
          <MenuItem value="to_contains">To contains</MenuItem>
          <MenuItem value="cc_contains">Cc contains</MenuItem>
          <MenuItem value="subject_contains">Subject contains</MenuItem>
          <MenuItem value="body_contains">Body contains</MenuItem>
          <MenuItem value="keyword">Keyword (headers + body)</MenuItem>
          <MenuItem value="has_attachment">Has attachment</MenuItem>
        </TextField>
        {form.condition_field !== 'has_attachment' && (
          <TextField
            label="Value"
            size="small"
            value={form.condition_value}
            onChange={(e) => setForm((p) => ({ ...p, condition_value: e.target.value }))}
          />
        )}
        <TextField
          select
          label="Action"
          size="small"
          value={form.action}
          onChange={(e) => setForm((p) => ({ ...p, action: e.target.value }))}
        >
          <MenuItem value="move">Move to folder</MenuItem>
          <MenuItem value="mark_read">Mark as read</MenuItem>
          <MenuItem value="mark_starred">Mark as starred</MenuItem>
          <MenuItem value="delete">Delete</MenuItem>
          <MenuItem value="archive">Archive</MenuItem>
        </TextField>
        {form.action === 'move' && (
          <TextField
            select
            label="Destination folder"
            size="small"
            value={form.action_folder}
            onChange={(e) => setForm((p) => ({ ...p, action_folder: e.target.value }))}
          >
            {folders.map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.imap_name}</MenuItem>
            ))}
          </TextField>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          }
          label="Active"
        />
        <Button
          variant="contained"
          onClick={() => createMutation.mutate()}
          disabled={!form.name || createMutation.isPending}
        >
          {createMutation.isPending ? <CircularProgress size={20} /> : 'Add rule'}
        </Button>
      </Stack>
    </Box>
  );
}

function GpgTab() {
  const qc = useQueryClient();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [keyData, setKeyData] = useState('');
  const [gen, setGen] = useState({ name: '', email: '', passphrase: '' });
  const [deleteTarget, setDeleteTarget] = useState<GpgKey | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['gpg-keys'],
    queryFn: getGpgKeys,
  });

  const importMutation = useMutation({
    mutationFn: () => importGpgKey(keyData),
    onSuccess: () => {
      setKeyData('');
      setMessage('Key imported.');
      setError('');
      qc.invalidateQueries({ queryKey: ['gpg-keys'] });
    },
    onError: (err: any) => setError(err?.response?.data?.detail || 'Import failed'),
  });

  const generateMutation = useMutation({
    mutationFn: () => generateGpgKey(gen),
    onSuccess: (r: any) => {
      setGen({ name: '', email: '', passphrase: '' });
      setMessage(`Key generated: ${r.fingerprint}`);
      setError('');
      qc.invalidateQueries({ queryKey: ['gpg-keys'] });
    },
    onError: (err: any) => setError(err?.response?.data?.detail || 'Generation failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ fp, passphrase }: { fp: string; passphrase: string }) =>
      deleteGpgKey(fp, passphrase),
    onSuccess: () => {
      setMessage('Key deleted.');
      setError('');
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ['gpg-keys'] });
    },
    onError: (err: any) =>
      setError(err?.response?.data?.detail || err?.message || 'Delete failed'),
  });

  const copyExport = async (fp: string) => {
    try {
      const exported = await exportGpgKey(fp, false);
      await navigator.clipboard.writeText(exported.key_data);
      setMessage('Public key copied to clipboard.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Export failed');
    }
  };

  return (
    <Box>
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        (data?.results || []).map((key) => (
          <Paper key={key.fingerprint} sx={{ p: 2, mb: 1 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                  {key.uids?.[0] || key.fingerprint}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {key.fingerprint}
                  {key.has_secret ? ' · secret key present' : ''}
                </Typography>
              </Box>
              <Tooltip title="Copy public key">
                <IconButton size="small" onClick={() => copyExport(key.fingerprint)} aria-label="Export key">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete key">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setError('');
                    if (key.has_secret) setDeleteTarget(key);
                    else deleteMutation.mutate({ fp: key.fingerprint, passphrase: '' });
                  }}
                  aria-label="Delete key"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        ))
      )}
      {(data?.results || []).length === 0 && !isLoading && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>No GnuPG keys yet.</Typography>
      )}

      <GpgPassphraseDialog
        open={Boolean(deleteTarget)}
        title="Delete secret key"
        description={`Enter the passphrase for ${deleteTarget?.uids?.[0] || deleteTarget?.fingerprint || 'this key'} to delete it.`}
        confirmLabel="Delete"
        busy={deleteMutation.isPending}
        error={error || null}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={(passphrase) => {
          if (!deleteTarget) return;
          deleteMutation.mutate({ fp: deleteTarget.fingerprint, passphrase });
        }}
      />

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Import key
      </Typography>
      <TextField
        multiline
        minRows={4}
        fullWidth
        size="small"
        placeholder="Paste ASCII-armored public or private key"
        value={keyData}
        onChange={(e) => setKeyData(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Button
        variant="outlined"
        onClick={() => importMutation.mutate()}
        disabled={!keyData.trim() || importMutation.isPending}
        sx={{ mb: 3 }}
      >
        Import
      </Button>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Generate keypair
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Name"
          size="small"
          value={gen.name}
          onChange={(e) => setGen((p) => ({ ...p, name: e.target.value }))}
        />
        <TextField
          label="Email"
          size="small"
          value={gen.email}
          onChange={(e) => setGen((p) => ({ ...p, email: e.target.value }))}
        />
        <TextField
          label="Passphrase (optional)"
          type="password"
          size="small"
          value={gen.passphrase}
          onChange={(e) => setGen((p) => ({ ...p, passphrase: e.target.value }))}
        />
        <Button
          variant="contained"
          onClick={() => generateMutation.mutate()}
          disabled={!gen.name || !gen.email || generateMutation.isPending}
        >
          {generateMutation.isPending ? <CircularProgress size={20} /> : 'Generate'}
        </Button>
      </Stack>
    </Box>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Settings
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} variant="scrollable">
        <Tab label="Mail" />
        <Tab label="Filter Rules" />
        <Tab label="GnuPG" />
      </Tabs>
      {tab === 0 && <MailAccountTab />}
      {tab === 1 && <FilterRulesTab />}
      {tab === 2 && <GpgTab />}
    </Box>
  );
}
